import { Injectable } from "@angular/core";
import { Translation, TranslocoInterceptor } from "@jsverse/transloco";

// The pseudo locale carries the English content (the loaders serve en for `pseudo`); this
// interceptor mangles the strings at load time so a non-speaking team can spot, at a glance,
// which strings are keyed (mangled) vs still hardcoded (plain English). See ADR 010.
export const PSEUDO_LANG = "pseudo";

const ACCENT_MAP: Record<string, string> = {
    a: "å",
    e: "é",
    i: "ì",
    o: "ó",
    u: "ú",
    n: "ñ",
    A: "Å",
    E: "É",
    I: "Ì",
    O: "Ó",
    U: "Ú",
    N: "Ñ",
};

export function pseudoize(value: string): string {
    // Idempotent: a re-loaded scope merges already-pseudo-ized values back through here.
    if (value.startsWith("[") && value.endsWith("␣]")) {
        return value;
    }
    // Accent letters only at brace depth 0. Everything inside {...} (ICU keywords, variable
    // names, plural sub-messages) is left verbatim so the ICU transpiler can still parse it.
    let depth = 0;
    let accented = "";
    for (const char of value) {
        if (char === "{") {
            depth++;
            accented += char;
        } else if (char === "}") {
            depth = Math.max(0, depth - 1);
            accented += char;
        } else {
            accented += depth === 0 ? (ACCENT_MAP[char] ?? char) : char;
        }
    }
    // Wrap to expose truncation/concatenation bugs and to mark the string as keyed.
    return `[${accented}␣]`;
}

@Injectable({ providedIn: "root" })
export class PseudoInterceptor implements TranslocoInterceptor {
    public preSaveTranslation(translation: Translation, lang: string): Translation {
        if (lang !== PSEUDO_LANG) {
            return translation;
        }
        const result: Translation = {};
        for (const key of Object.keys(translation)) {
            const value = translation[key];
            result[key] = typeof value === "string" ? pseudoize(value) : value;
        }
        return result;
    }

    public preSaveTranslationKey(key: string, value: string, lang: string): string {
        return lang === PSEUDO_LANG && typeof value === "string" ? pseudoize(value) : value;
    }
}
